/**
 * TradingView Chart Modal Component
 *
 * A reusable modal that displays a TradingView chart for any ticker symbol.
 * Uses the TradingView Widget API for embedding charts.
 */

import React, { useMemo, useState, createContext, useContext, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { X, ExternalLink, Loader2 } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

interface TradingViewModalContextType {
  openChart: (symbol: string) => void;
  closeChart: () => void;
  isOpen: boolean;
  currentSymbol: string | null;
}

interface TradingViewModalProps {
  open: boolean;
  symbol: string | null;
  onClose: () => void;
}

type TimeInterval = '1' | '5' | '15' | '60' | 'D' | 'W' | 'M';

// =============================================================================
// Context
// =============================================================================

const TradingViewModalContext = createContext<TradingViewModalContextType | null>(null);

export const useTradingViewModal = () => {
  const context = useContext(TradingViewModalContext);
  if (!context) {
    throw new Error('useTradingViewModal must be used within a TradingViewModalProvider');
  }
  return context;
};

// =============================================================================
// Provider Component
// =============================================================================

export const TradingViewModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentSymbol, setCurrentSymbol] = useState<string | null>(null);

  const openChart = useCallback((symbol: string) => {
    // Normalize symbol format (remove exchange prefix if present, then add it)
    const cleanSymbol = symbol.replace(/^(NASDAQ:|NYSE:|AMEX:)/, '');
    setCurrentSymbol(cleanSymbol);
    setIsOpen(true);
  }, []);

  const closeChart = useCallback(() => {
    setIsOpen(false);
    // Delay clearing symbol to allow for smooth close animation
    setTimeout(() => setCurrentSymbol(null), 300);
  }, []);

  return (
    <TradingViewModalContext.Provider value={{ openChart, closeChart, isOpen, currentSymbol }}>
      {children}
      <TradingViewModal open={isOpen} symbol={currentSymbol} onClose={closeChart} />
    </TradingViewModalContext.Provider>
  );
};

// =============================================================================
// Clickable Ticker Component
// =============================================================================

interface ClickableTickerProps {
  symbol: string;
  children?: React.ReactNode;
  variant?: 'text' | 'chip';
  color?: 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  size?: 'small' | 'medium';
}

export const ClickableTicker: React.FC<ClickableTickerProps> = ({
  symbol,
  children,
  variant = 'text',
  color = 'primary',
  size = 'small',
}) => {
  const { openChart } = useTradingViewModal();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openChart(symbol);
  };

  if (variant === 'chip') {
    return (
      <Badge
        className="cursor-pointer hover:opacity-85"
        onClick={handleClick}
      >
        {children || symbol}
      </Badge>
    );
  }

  return (
    <span
      onClick={handleClick}
      className="cursor-pointer text-primary font-semibold hover:underline hover:text-primary/80"
    >
      {children || symbol}
    </span>
  );
};

// =============================================================================
// Modal Component
// =============================================================================

const INTERVALS: { value: TimeInterval; label: string }[] = [
  { value: '1', label: '1m' },
  { value: '5', label: '5m' },
  { value: '15', label: '15m' },
  { value: '60', label: '1h' },
  { value: 'D', label: '1D' },
  { value: 'W', label: '1W' },
  { value: 'M', label: '1M' },
];

const TradingViewModal: React.FC<TradingViewModalProps> = ({ open, symbol, onClose }) => {
  const [interval, setInterval] = useState<TimeInterval>('D');
  const [loading, setLoading] = useState(true);
  // Detect dark mode from the document class (Tailwind convention)
  const tvTheme = typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light';

  const getFullSymbol = (sym: string): string => {
    const cleanSym = (sym || '').toUpperCase().trim();
    if (cleanSym.includes(':')) return cleanSym;
    // Normalize forex underscores (AUD_USD -> AUDUSD) for TradingView
    if (/^[A-Z]{3}_[A-Z]{3}$/.test(cleanSym)) return `FX:${cleanSym.replace('_', '')}`;
    // Normalize crypto dashes (BTC-USD -> BTCUSD)
    if (cleanSym.endsWith('-USD')) return `COINBASE:${cleanSym.replace('-', '')}`;
    return cleanSym;
  };

  // Build the TradingView embed iframe URL with config in the hash fragment
  const iframeSrc = useMemo(() => {
    if (!symbol) return '';
    const config = {
      autosize: true,
      symbol: getFullSymbol(symbol),
      interval,
      timezone: 'America/New_York',
      theme: tvTheme,
      style: '1',
      locale: 'en',
      enable_publishing: false,
      allow_symbol_change: true,
      calendar: false,
      hide_top_toolbar: false,
      hide_legend: false,
      save_image: true,
      studies: ['RSI@tv-basicstudies', 'MASimple@tv-basicstudies'],
      support_host: 'https://www.tradingview.com',
    };
    return `https://s.tradingview.com/embed-widget/advanced-chart/?locale=en#${encodeURIComponent(JSON.stringify(config))}`;
  }, [symbol, interval, tvTheme]);

  const handleIntervalChange = (newInterval: TimeInterval) => {
    setLoading(true);
    setInterval(newInterval);
  };

  const handleOpenTradingView = () => {
    if (symbol) {
      window.open(`https://www.tradingview.com/chart/?symbol=${getFullSymbol(symbol)}`, '_blank');
    }
  };

  if (!symbol) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-[95vw] w-[1200px] h-[80vh] max-h-[850px] p-0 gap-0 flex flex-col [&>button:last-child]:hidden">
        <div className="px-4 py-3 flex items-center justify-between border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-base font-bold font-display">
              {symbol}
            </DialogTitle>
            <div className="flex items-center gap-0.5 bg-muted rounded-md p-0.5">
              {INTERVALS.map((item) => (
                <button
                  key={item.value}
                  onClick={() => handleIntervalChange(item.value)}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                    interval === item.value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleOpenTradingView} title="Open in TradingView">
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} title="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="relative flex-1 min-h-0">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {open && iframeSrc && (
            <iframe
              src={iframeSrc}
              onLoad={() => setLoading(false)}
              className="w-full h-full border-none block"
              title={`TradingView chart for ${symbol}`}
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TradingViewModal;
